import React from 'react';
import {
	type FieldPath,
	type FieldValues,
	type PathValue,
} from 'react-hook-form';
import type {SetRequired} from 'type-fest';
import {Checkbox, type CheckboxProps} from '@netx/core-components/Checkbox';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export {checkboxClasses} from '@netx/core-components/Checkbox';

export interface ReactHookFormCheckboxProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
	SetRequired<Omit<CheckboxProps, 'checked'>, 'name'>,
	TFieldValues,
	TName
> {
	/** hook for parsing the value from the form to the input */
	parseValueIn?: (value: PathValue<TFieldValues, TName>) => boolean;
	/** hook for parsing the value from the input to the form */
	parseValueOut?: (value: boolean) => PathValue<TFieldValues, TName>;
	overridenValue?: boolean;
}

export const ReactHookFormCheckboxPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		parseValueIn,
		parseValueOut,
		overridenValue,
		...props
	}: ReactHookFormCheckboxProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => (
				<Checkbox
					error={formState.errors[props.name]?.message?.toString()}
					{...props}
					{...field}
					readOnly={props.readOnly}
					// TODO: just use one...
					value={
						overridenValue != null
							? overridenValue
							: (parseValueIn ? parseValueIn(field.value) : field.value) ||
								false
					}
					checked={
						overridenValue != null
							? overridenValue
							: (parseValueIn ? parseValueIn(field.value) : field.value) ||
								false
					}
					disabled={field.disabled || props.disabled}
					ref={ref}
					inputRef={combineRefs(props.inputRef, field.ref)}
					onChange={useConstCallback(
						splitEventHandler(props.onChange, (event, checked) => {
							field.onChange(parseValueOut ? parseValueOut(checked) : checked);
						}),
					)}
				/>
			)}
			rules={{
				required: props.required,
				validate: async (value) => {
					return props.required && !value ? i18n.t('i18n.required') : true;
				},
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `Checkbox` component
 */
export const ReactHookFormCheckbox = React.forwardRef(
	ReactHookFormCheckboxPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormCheckboxProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormCheckbox;
